'use client';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios, { AxiosInstance } from 'axios';
import { ConnectionHandler } from './connection-handler';
import { use } from 'sst/constructs';

function tryAndDefault<T>(exec: () => T, defaultValue: T) {
  try {
    return exec();
  } catch (e) {
    console.error('Error occurred: ', e);
    return defaultValue;
  }
}

export type ConnectionType = AxiosInstance & {
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
};

export type ConnectionData = {
  axiosInstance: ConnectionType;
  user?: any | null | undefined;
};

export const Connection = React.createContext<ConnectionData>({} as any);

type ConnectionProviderProps = {
  baseUrl: string;
  cookieDomain: string;
  refreshToken?: string;
  profile?: string;
  loading?: React.ReactElement;
};

export const ConnectionProvider: FC<
  PropsWithChildren<ConnectionProviderProps>
> = (props) => {
  const [user, setUser] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [connection, setConnection] = useState<ConnectionHandler>(
    new ConnectionHandler(setUser),
  );
  const val = useMemo(() => {
    return { axiosInstance: connection.axiosInstance, user };
  }, [connection, user]);
  useEffect(() => {
    setConnection((prevState) => {
      prevState.close();
      const conn = new ConnectionHandler(setUser);
      setLoading(true);
      conn
        .init({
          baseUrl: props.baseUrl,
          domain: props.cookieDomain,
          profile: props.profile,
        })
        .then(() => setLoading(false));
      return conn;
    });
  }, [props.baseUrl, props.cookieDomain, props.profile]);
  if (loading) {
    return <div>Loading...</div>;
  }
  return (
    <Connection.Provider value={val}>{props.children}</Connection.Provider>
  );
};

export const useConnection = () => {
  return useContext(Connection).axiosInstance;
};

export const useUser = <T = any,>(): T | undefined => {
  return useContext(Connection).user;
};
